import { Card, Icon, Meter, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney, money } from '@/lib/money'
import dash from '@/features/dashboard/DashboardScreen.module.css'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

export function InsightsScreen() {
  const repository = useRepository()
  const spending = useQuery(() => repository.getSpendingBreakdown(), [repository])
  const cashflow = useQuery(() => repository.getCashflow(), [repository])

  const peak = cashflow.data
    ? Math.max(...cashflow.data.flatMap((point) => [point.inbound.minorUnits, point.outbound.minorUnits]))
    : 1
  const totalOut = spending.data?.reduce((sum, slice) => sum + slice.amount.minorUnits, 0) ?? 0
  const sentHome = spending.data?.find((slice) => slice.category === 'Sent abroad')

  return (
    <>
      <PageHeader
        title="Insights"
        subtitle="Where the money goes, and what it costs you to move it."
      />

      <BentoGrid>
        {[
          { label: 'Spent this month', value: formatMoney(money(totalOut, 'USD')), note: '−4.2% vs August' },
          { label: 'Sent home', value: sentHome ? formatMoney(sentHome.amount) : '—', note: 'Across 3 transfers' },
          { label: 'Fees you paid', value: '$18.40', note: '0.62% of what you moved' },
          { label: 'Cashback earned', value: '$1.56', note: 'Paid on 1 October' },
        ].map((stat) => (
          <Card key={stat.label} tone="light">
            <Text variant="eyebrow" weight="semibold" tone="secondary">
              {stat.label}
            </Text>
            <Text as="p" variant="heading" weight="bold" numeric className="pb-mt-2">
              {stat.value}
            </Text>
            <Text variant="eyebrow" tone="secondary" className="pb-mt-2">
              {stat.note}
            </Text>
          </Card>
        ))}

        <BentoSpan span={2}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">
                Money in and out
              </Text>
              <div className={dash.legend}>
                <span className={dash.legendItem}>
                  <span className={dash.swatch} style={{ background: 'var(--pb-accent)' }} />
                  <Text variant="eyebrow" tone="secondary">In</Text>
                </span>
                <span className={dash.legendItem}>
                  <span className={dash.swatch} style={{ background: 'var(--pb-ink)' }} />
                  <Text variant="eyebrow" tone="secondary">Out</Text>
                </span>
              </div>
            </Card.Header>
            <div className="pb-mt-5">
              <div className={dash.chart}>
                {cashflow.data?.map((point) => (
                  <div key={point.label} className={dash.column}>
                    <div className={`${dash.bar} ${dash.barIn}`} style={{ height: `${(point.inbound.minorUnits / peak) * 100}%` }} />
                    <div className={`${dash.bar} ${dash.barOut}`} style={{ height: `${(point.outbound.minorUnits / peak) * 100}%` }} />
                  </div>
                ))}
              </div>
              <div className={dash.axis}>
                {cashflow.data?.map((point) => (
                  <Text key={point.label} variant="eyebrow" tone="secondary" className={dash.axisLabel}>
                    {point.label}
                  </Text>
                ))}
              </div>
            </div>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Where it went
            </Text>
            <Stack gap={4} className="pb-mt-4">
              {spending.data?.map((slice) => (
                <Stack key={slice.category} gap={2}>
                  <Stack direction="row" align="center" gap={3}>
                    <Icon name={slice.icon} size={16} />
                    <Text variant="caption" weight="semibold" className="pb-grow" truncate>
                      {slice.category}
                    </Text>
                    <Text variant="caption" tone="secondary" numeric>
                      {formatMoney(slice.amount, { compactDecimals: true })}
                    </Text>
                  </Stack>
                  <Meter value={slice.share} height={6} tone="ink" label={slice.category} />
                </Stack>
              ))}
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={4}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                What moving money cost you
              </Text>
              <Pill tone="accent">0.62% all in</Pill>
            </Card.Header>
            <Stack gap={0} className="pb-mt-3">
              {[
                ['Sending abroad · 1.0%', '$15.00'],
                ['FX spread on the corridor', 'in the rate'],
                ['Card spending that converted · 0.9%', '$3.40'],
                ['Everything else', '$0.00'],
              ].map(([label, value]) => (
                <div key={label} className={checkout.line}>
                  <Text variant="caption" tone="onDarkMuted">{label}</Text>
                  <Text variant="caption" weight="semibold" tone="onDark" numeric>{value}</Text>
                </div>
              ))}
            </Stack>
            <Text variant="eyebrow" tone="onDarkMuted" className="pb-mt-4">
              Remitly charges about 2.11% all in. We show ours here so you can check the claim.
            </Text>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
